import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { RefreshCw, Filter } from "lucide-react";
import { http, api } from "@/lib/api";
import HeatmapStadium from "@/components/crowd/HeatmapStadium";
import GateThroughputChart from "@/components/crowd/GateThroughputChart";
import ZoneOccupancyCard from "@/components/crowd/ZoneOccupancyCard";
import CrowdSummary from "@/components/crowd/CrowdSummary";
import ZoneDetailsDrawer from "@/components/command/ZoneDetailsDrawer";

const FILTERS = [
  { id: "all",       label: "All" },
  { id: "stand",     label: "Stands" },
  { id: "gate",      label: "Gates" },
  { id: "concourse", label: "Concourse" },
  { id: "vip",       label: "VIP" },
  { id: "parking",   label: "Parking" },
];

const REFRESH_MS = 6000;

export default function Crowd() {
  const { bootstrap } = useOutletContext();
  const [heatmap, setHeatmap] = useState(null);
  const [throughput, setThroughput] = useState(null);
  const [flows, setFlows] = useState(null);
  const [summary, setSummary] = useState(null);
  const [zoneId, setZoneId] = useState(null);
  const [filter, setFilter] = useState("all");
  const [refreshing, setRefreshing] = useState(false);
  const [histByZone, setHistByZone] = useState({}); // zoneId → history points

  const fetchAll = async () => {
    setRefreshing(true);
    try {
      const [hm, tp, fl, sm] = await Promise.all([
        api.heatmap(),
        http.get("/crowd/throughput").then((r) => r.data),
        http.get("/crowd/flows").then((r) => r.data),
        http.get("/crowd/summary").then((r) => r.data),
      ]);
      setHeatmap(hm);
      setThroughput(tp);
      setFlows(fl);
      setSummary(sm);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAll();
    const t = setInterval(fetchAll, REFRESH_MS);
    return () => clearInterval(t);
  }, []);

  // Lazy-load per-zone history once we know zones (used for sparklines)
  useEffect(() => {
    const ids = (bootstrap?.zones ?? []).map((z) => z.id);
    if (!ids.length) return;
    let cancelled = false;
    (async () => {
      const out = {};
      // limit parallel fetches
      for (const id of ids) {
        try {
          const d = await api.zone(id);
          if (cancelled) return;
          out[id] = d.history ?? [];
        } catch {}
      }
      if (!cancelled) setHistByZone(out);
    })();
    return () => { cancelled = true; };
  }, [bootstrap]);

  const zonesWithCurrent = useMemo(() => {
    const zones = bootstrap?.zones ?? [];
    const hmById = Object.fromEntries((heatmap?.zones ?? []).map((z) => [z.zone_id, z]));
    return zones.map((z) => ({ ...z, current_occupancy: hmById[z.id]?.occupancy ?? z.current_occupancy }));
  }, [bootstrap, heatmap]);

  const filteredZones = useMemo(() => {
    if (filter === "all") return zonesWithCurrent;
    return zonesWithCurrent.filter((z) => z.type === filter);
  }, [zonesWithCurrent, filter]);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Slim title row */}
      <div className="h-10 flex items-center justify-between px-4 border-b border-border bg-[hsl(var(--surface))] shrink-0">
        <div className="flex items-baseline gap-3">
          <h1 className="font-display font-black uppercase tracking-tight text-sm leading-none">
            Crowd Intelligence
          </h1>
          <span className="micro-label hidden sm:inline">Density · Flow · Throughput</span>
        </div>
        <button
          onClick={fetchAll}
          className="btn-tac !px-2 !py-1.5"
          data-testid="crowd-refresh-btn"
          aria-label="Refresh crowd data"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
          <span className="hidden sm:inline">{refreshing ? "syncing…" : "refresh"}</span>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-2 space-y-2 min-h-0">
        <CrowdSummary summary={summary} />

        {/* Heatmap + Throughput */}
        <div className="grid grid-cols-1 xl:grid-cols-5 gap-2">
          <div className="panel xl:col-span-3 min-h-[420px] flex flex-col">
            <div className="flex items-center justify-between px-3 py-1.5 border-b border-border shrink-0">
              <span className="h-widget">Density Heatmap & Flows</span>
              <span className="font-mono text-[10px] text-muted-foreground">
                {heatmap?.zones?.length ?? 0} zones · {flows?.flows?.length ?? 0} flow vectors
              </span>
            </div>
            <div className="flex-1 grid-bg p-2 min-h-0">
              <HeatmapStadium
                zones={heatmap?.zones ?? []}
                flows={flows?.flows ?? []}
                onZoneClick={(id) => setZoneId(id)}
              />
            </div>
          </div>

          <div className="panel xl:col-span-2 min-h-[420px]">
            <GateThroughputChart series={throughput?.series ?? []} />
          </div>
        </div>

        {/* Zone Occupancy cards */}
        <div className="panel">
          <div className="flex items-center justify-between px-3 py-1.5 border-b border-border flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <span className="h-widget">Zone Occupancy</span>
              <span className="font-mono text-[10px] text-muted-foreground">{filteredZones.length} / {zonesWithCurrent.length}</span>
            </div>
            <div className="flex items-center gap-1 flex-wrap">
              <Filter className="w-3.5 h-3.5 text-muted-foreground" />
              {FILTERS.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  data-testid={`crowd-filter-${f.id}`}
                  className={[
                    "px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold border transition-colors",
                    filter === f.id
                      ? "border-[hsl(var(--primary))] text-[hsl(var(--primary))]"
                      : "border-border text-muted-foreground hover:text-foreground",
                  ].join(" ")}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          <div
            className="grid gap-2 p-2"
            style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}
            data-testid="zone-occupancy-grid"
          >
            {filteredZones.map((z) => (
              <ZoneOccupancyCard
                key={z.id}
                zone={z}
                history={histByZone[z.id]}
                onClick={() => setZoneId(z.id)}
              />
            ))}
            {filteredZones.length === 0 && (
              <div className="col-span-full p-6 text-center text-xs text-muted-foreground">
                No zones match this filter.
              </div>
            )}
          </div>
        </div>
      </div>

      <ZoneDetailsDrawer zoneId={zoneId} onClose={() => setZoneId(null)} />
    </div>
  );
}
